import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import DashboardLayout from '../components/DashboardLayout';
import { Mail, Lock, User, Save, Phone, Globe, MapPin, Search, Clock, ShieldCheck, Snowflake, Trash2, Bell } from 'lucide-react';
import { getCityOptions, getTimezoneDisplayName } from '../data/canadaCities';
import { getUTCOffset } from '../utils/timezoneUtils';
import { Switch } from '../components/ui/switch';

const UserProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateTimezone, logout } = useAuth();
  const [emailData, setEmailData] = useState({
    email: user?.email || '',
    password: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [phoneData, setPhoneData] = useState({
    phone: user?.phone || ''
  });
  const [loading, setLoading] = useState({ email: false, password: false, phone: false, timezone: false, notifications: false });

  // Notification preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    groupClassNotifications: true
  });

  // Timezone state
  const [cityOptions, setCityOptions] = useState([]);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Load city options on mount
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const options = await getCityOptions();
        setCityOptions(options);
        // If user already has timezone, find and select their current city
        if (user?.city && user?.timezone) {
          const currentCity = options.find(opt => 
            opt.city === user.city && opt.timezone === user.timezone
          );
          if (currentCity) {
            setSelectedCity(currentCity);
          }
        }
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };
    fetchCities();
  }, [user?.city, user?.timezone]);

  // Load notification preferences
  useEffect(() => {
    const fetchNotificationPrefs = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${API_BASE}/api/users/me/notification-preferences`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setNotificationPrefs({
            groupClassNotifications: data.group_class_notifications ?? true
          });
        }
      } catch (error) {
        console.error('Error fetching notification preferences:', error);
      }
    };
    fetchNotificationPrefs();
  }, [API_BASE]);

  const handleToggleGroupClassNotifications = async (enabled) => {
    setLoading({ ...loading, notifications: true });
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/users/me/notification-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          group_class_notifications: enabled
        })
      });

      if (response.ok) {
        setNotificationPrefs({ ...notificationPrefs, groupClassNotifications: enabled });
        toast({
          title: 'Success',
          description: enabled 
            ? 'Group class notifications enabled' 
            : 'Group class notifications disabled'
        });
      } else {
        throw new Error('Failed to update notification preferences');
      }
    } catch (error) {
      console.error('Notification update error:', error);
      toast({
        title: 'Error',
        description: 'Failed to update notification preferences',
        variant: 'destructive'
      });
      // Revert the toggle on error
      setNotificationPrefs({ ...notificationPrefs, groupClassNotifications: !enabled });
    } finally {
      setLoading({ ...loading, notifications: false });
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCityDropdownOpen && !event.target.closest('.timezone-dropdown')) {
        setIsCityDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCityDropdownOpen]);

  // Filter cities based on search query
  const filteredCities = useMemo(() => {
    if (!citySearchQuery) return cityOptions.slice(0, 50);
    const query = citySearchQuery.toLowerCase();
    return cityOptions.filter(opt => 
      opt.city.toLowerCase().includes(query) ||
      opt.province.toLowerCase().includes(query)
    ).slice(0, 100);
  }, [cityOptions, citySearchQuery]);

  const handleCitySelect = (option) => {
    setSelectedCity(option);
    setCitySearchQuery('');
    setIsCityDropdownOpen(false);
  };

  const handleUpdateTimezone = async (e) => {
    e.preventDefault();
    
    if (!selectedCity) {
      toast({
        title: 'Error',
        description: 'Please select a city',
        variant: 'destructive'
      });
      return;
    }

    setLoading({ ...loading, timezone: true });

    try {
      const result = await updateTimezone({
        city: selectedCity.city,
        province: selectedCity.province,
        timezone: selectedCity.timezone,
      });

      if (result.success) {
        toast({
          title: 'Success',
          description: 'Timezone updated successfully'
        });
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Failed to update timezone',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An error occurred while updating timezone',
        variant: 'destructive'
      });
    } finally {
      setLoading({ ...loading, timezone: false });
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    
    if (!emailData.email || !emailData.password) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        variant: 'destructive'
      });
      return;
    }

    setLoading({ ...loading, email: true });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/auth/update-email`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: emailData.email,
          password: emailData.password
        })
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse response as JSON:', jsonError);
        data = {};
      }

      if (response.ok) {
        toast({
          title: 'Success',
          description: data.message || 'Email updated successfully'
        });
        setEmailData({ ...emailData, password: '' });
      } else {
        toast({
          title: 'Error',
          description: data.detail || data.message || `Failed to update email (Status: ${response.status})`,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Email update error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Network error while updating email',
        variant: 'destructive'
      });
    } finally {
      setLoading({ ...loading, email: false });
    }
  };

  const handleUpdatePhone = async (e) => {
    e.preventDefault();
    
    if (!phoneData.phone) {
      toast({
        title: 'Error',
        description: 'Please enter a phone number',
        variant: 'destructive'
      });
      return;
    }

    setLoading({ ...loading, phone: true });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          phone: phoneData.phone
        })
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse response as JSON:', jsonError);
        data = {};
      }

      if (response.ok) {
        toast({
          title: 'Success',
          description: data.message || 'Phone number updated successfully'
        });
      } else {
        toast({
          title: 'Error',
          description: data.detail || data.message || `Failed to update phone number (Status: ${response.status})`,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Phone update error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Network error while updating phone number',
        variant: 'destructive'
      });
    } finally {
      setLoading({ ...loading, phone: false });
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        variant: 'destructive'
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'New passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }

    setLoading({ ...loading, password: true });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/auth/update-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse response as JSON:', jsonError);
        data = {};
      }

      if (response.ok) {
        toast({
          title: 'Success',
          description: data.message || 'Password updated successfully'
        });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        toast({
          title: 'Error',
          description: data.detail || data.message || `Failed to update password (Status: ${response.status})`,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Password update error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Network error while updating password',
        variant: 'destructive'
      });
    } finally {
      setLoading({ ...loading, password: false });
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-black">
            Profile Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Manage your account settings and preferences</p>
        </div>

        {/* User Info */}
        <Card className="border-2 border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              User Information
            </CardTitle>
            <CardDescription>Your current account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Name</Label>
              <Input value={user?.name || ''} disabled className="mt-1 bg-gray-50" />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Current Email</Label>
              <Input value={user?.email || ''} disabled className="mt-1 bg-gray-50" />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Role</Label>
              <Input value={user?.role || ''} disabled className="mt-1 bg-gray-50 capitalize" />
            </div>
          </CardContent>
        </Card>

        {/* Timezone Settings */}
        <Card className="border-2 border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Timezone Settings
            </CardTitle>
            <CardDescription>
              Set your timezone for accurate booking times across Canada&apos;s 6 time zones
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateTimezone} className="space-y-4">
              {/* Current Timezone Display */}
              {user?.timezone && (
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <Clock className="w-4 h-4 text-red-600" />
                    <span>Current timezone: <strong>{user.city}, {user.province}</strong></span>
                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                      {getUTCOffset(user.timezone)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-6">
                    {getTimezoneDisplayName(user.timezone)}
                  </p>
                </div>
              )}
              
              {/* City Selector */}
              <div className="timezone-dropdown">
                <Label className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-red-600" />
                  Select Your City
                </Label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      value={isCityDropdownOpen ? citySearchQuery : (selectedCity ? `${selectedCity.city}, ${selectedCity.province}` : '')}
                      onChange={(e) => {
                        setCitySearchQuery(e.target.value);
                        setIsCityDropdownOpen(true);
                      }}
                      onFocus={() => setIsCityDropdownOpen(true)}
                      placeholder="Search for your city..."
                      className="pl-10 border-2 border-gray-200 dark:border-gray-600 focus:border-red-500"
                    />
                  </div>
                  {isCityDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredCities.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                          {citySearchQuery ? 'No cities found' : 'Loading cities...'}
                        </div>
                      ) : (
                        filteredCities.map((option, idx) => (
                          <button
                            key={`${option.city}-${option.province}-${idx}`}
                            type="button"
                            onClick={() => handleCitySelect(option)}
                            className={`w-full px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                              selectedCity?.city === option.city && selectedCity?.province === option.province 
                                ? 'bg-red-50 dark:bg-gray-700' 
                                : ''
                            }`}
                          >
                            <div className="font-medium text-gray-900 dark:text-white">{option.city}, {option.province}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{option.timezoneDisplay}</div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected City Preview */}
              {selectedCity && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-green-800 dark:text-green-300">{selectedCity.city}, {selectedCity.province}</p>
                        <span className="px-2 py-0.5 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 rounded text-xs font-medium">
                          {getUTCOffset(selectedCity.timezone)}
                        </span>
                      </div>
                      <p className="text-sm text-green-600 dark:text-green-400">{selectedCity.timezoneDisplay}</p>
                    </div>
                  </div>
                </div>
              )}

              <Button 
                type="submit" 
                disabled={loading.timezone || !selectedCity}
                className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading.timezone ? 'Updating...' : 'Update Timezone'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card className="border-2 border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notification Settings
            </CardTitle>
            <CardDescription>Manage your email notification preferences</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Group Class Notifications */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg mt-0.5">
                  <Bell className="w-4 h-4 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Group Class Notifications</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Receive email reminders for group classes you&apos;ve joined
                  </p>
                  <ul className="text-xs text-gray-400 dark:text-gray-500 mt-2 space-y-1">
                    <li>• 24 hours before class starts</li>
                    <li>• 1 hour before class starts</li>
                    <li>• When a class is cancelled</li>
                  </ul>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {loading.notifications && (
                  <span className="text-xs text-gray-400">Saving...</span>
                )}
                <Switch
                  checked={notificationPrefs.groupClassNotifications}
                  onCheckedChange={handleToggleGroupClassNotifications}
                  disabled={loading.notifications}
                  data-testid="group-class-notifications-toggle"
                />
              </div>
            </div>

            {/* Info box */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                <strong>Note:</strong> You will always receive important account-related emails regardless of these settings.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Update Phone Number */}
        <Card className="border-2 border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Phone Number
            </CardTitle>
            <CardDescription>Add or update your contact phone number</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdatePhone} className="space-y-4">
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phoneData.phone}
                  onChange={(e) => setPhoneData({ phone: e.target.value })}
                  placeholder="Enter your phone number (e.g., +1 234 567 8900)"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Include country code for better reach</p>
              </div>
              <Button 
                type="submit" 
                disabled={loading.phone}
                className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading.phone ? 'Updating...' : 'Update Phone Number'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Update Email */}
        <Card className="border-2 border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Update Email
            </CardTitle>
            <CardDescription>Change your email address</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateEmail} className="space-y-4">
              <div>
                <Label htmlFor="new-email">New Email Address</Label>
                <Input
                  id="new-email"
                  type="email"
                  value={emailData.email}
                  onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                  placeholder="Enter new email"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email-password">Current Password</Label>
                <Input
                  id="email-password"
                  type="password"
                  value={emailData.password}
                  onChange={(e) => setEmailData({ ...emailData, password: e.target.value })}
                  placeholder="Enter your current password"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Confirm your identity with your password</p>
              </div>
              <Button 
                type="submit" 
                disabled={loading.email}
                className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading.email ? 'Updating...' : 'Update Email'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Update Password */}
        <Card className="border-2 border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Update Password
            </CardTitle>
            <CardDescription>Change your account password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div>
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter your current password"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  className="mt-1"
                />
              </div>
              <Button 
                type="submit" 
                disabled={loading.password}
                className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading.password ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Account Management */}
        <UserAccountManagement navigate={navigate} logout={logout} API_BASE={API_BASE} />
      </div>
    </DashboardLayout>
  );
};

// Account Management Component for Users
const UserAccountManagement = ({ navigate, logout, API_BASE }) => {
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [showUnfreezeModal, setShowUnfreezeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [accountStatus, setAccountStatus] = useState('active');
  const [loading, setLoading] = useState(false);

  const fetchAccountStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/user/account/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAccountStatus(data.status);
      }
    } catch (error) {
      console.error('Error fetching account status:', error);
    }
  };

  useEffect(() => {
    fetchAccountStatus();
  }, []);

  const handleFreeze = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/user/account/freeze`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'Account Frozen',
          description: 'Your account has been frozen. You can unfreeze it anytime.',
        });
        setShowFreezeModal(false);
        fetchAccountStatus();
      } else {
        throw new Error('Failed to freeze account');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to freeze account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnfreeze = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/user/account/unfreeze`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'Account Reactivated',
          description: 'Your account is now active again!',
        });
        setShowUnfreezeModal(false);
        fetchAccountStatus();
      } else {
        throw new Error('Failed to unfreeze account');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reactivate account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE MY ACCOUNT') {
      toast({
        title: 'Error',
        description: 'Please type exactly: DELETE MY ACCOUNT',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/user/account?confirmation_text=${encodeURIComponent(confirmText)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'Account Deleted',
          description: 'Your account has been permanently deleted. Logging you out...',
        });
        
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        logout();
        
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete account');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-2 border-red-200">
      <CardHeader className="bg-gradient-to-r from-red-50 to-white dark:from-gray-800 dark:to-gray-800">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
            <ShieldCheck className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <CardTitle>Account Management</CardTitle>
            <CardDescription>Manage your account security and privacy settings</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        {/* Current Status Alert */}
        {accountStatus === 'frozen' && (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 rounded-lg">
            <div className="flex items-start gap-3">
              <Snowflake className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-300">
                  Your account is currently frozen
                </p>
                <p className="text-xs text-yellow-800 dark:text-yellow-400 mt-1">
                  Click below to reactivate your account.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Freeze/Unfreeze Account */}
        <div className="p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-gray-300 dark:hover:border-gray-500 transition-colors">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${accountStatus === 'frozen' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-yellow-100 dark:bg-yellow-900/30'}`}>
              <Snowflake className={`w-5 h-5 ${accountStatus === 'frozen' ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                {accountStatus === 'frozen' ? 'Reactivate Your Account' : 'Freeze Your Account'}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {accountStatus === 'frozen' 
                  ? 'Unfreeze your account to continue using all features.'
                  : 'Temporarily pause your account. You can reactivate anytime.'
                }
              </p>
              <Button
                variant={accountStatus === 'frozen' ? 'default' : 'outline'}
                size="sm"
                onClick={() => accountStatus === 'frozen' ? setShowUnfreezeModal(true) : setShowFreezeModal(true)}
                className={accountStatus === 'frozen' 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'border-2 border-yellow-400 dark:border-yellow-600 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                }
              >
                {accountStatus === 'frozen' ? (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Unfreeze Account
                  </>
                ) : (
                  <>
                    <Snowflake className="w-4 h-4 mr-2" />
                    Freeze Account
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Delete Account */}
        <div className="p-4 border-2 border-red-200 dark:border-red-700 rounded-lg bg-red-50 dark:bg-red-900/20">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-red-700 dark:text-red-300 mb-1">Delete Your Account</h4>
              <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                Permanently remove your account and all associated data. This action cannot be undone.
              </p>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteModal(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
            </div>
          </div>
        </div>

        {/* Freeze Modal */}
        {showFreezeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Freeze Your Account?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Your account will be temporarily paused. You can unfreeze it anytime.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowFreezeModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleFreeze} disabled={loading} className="flex-1 bg-yellow-600 hover:bg-yellow-700">
                  {loading ? 'Freezing...' : 'Freeze Account'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Unfreeze Modal */}
        {showUnfreezeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Reactivate Your Account?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Your account will become active again.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowUnfreezeModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleUnfreeze} disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700">
                  {loading ? 'Activating...' : 'Reactivate Account'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">Delete Account Permanently?</h3>
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This will permanently delete your account. This action cannot be undone.
                </p>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                    ⚠️ Warning: All your data will be permanently deleted including:
                  </p>
                  <ul className="text-xs text-red-600 dark:text-red-400 mt-2 ml-4 list-disc">
                    <li>Your profile and bookings</li>
                    <li>Reviews you have written</li>
                    <li>Messages and conversations</li>
                  </ul>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2">Type &quot;DELETE MY ACCOUNT&quot; to confirm:</Label>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="DELETE MY ACCOUNT"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => { setShowDeleteModal(false); setConfirmText(''); }} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDelete} 
                  disabled={loading || confirmText !== 'DELETE MY ACCOUNT'}
                  className="flex-1"
                >
                  {loading ? 'Deleting...' : 'Delete Forever'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserProfilePage;
